import Phaser from 'phaser';
import BattleStateManager from '../game/BattleStateManager.js';
import sessionStateManager from '../game/SessionStateManager.js';

export class BattlefieldLootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BattlefieldLootScene' });
    this.displayDuration = 10000; // 10 seconds
    this.startTime = null;
    this.battlefieldLootData = null;
  }

  init(data) {
    // Get battle ID from data or BattleStateManager
    this.battleId = data?.battleId || BattleStateManager.getBattleId();
    this.battlefieldInstanceId = data?.battlefieldInstanceId || null;
    
    // Check if dummy data is provided (for testing)
    this.dummyData = data?.dummyData || null;
    
    // Validate battle ID format (should be UUID, not temporary battle_xxx format)
    if (this.battleId && this.battleId.startsWith('battle_')) {
      console.warn('⚠️ BattlefieldLootScene: Received temporary battle ID format:', this.battleId);
      console.warn('⚠️ This may indicate BattleStateManager was not updated with the real battle ID');
    }
    
    console.log('🏴‍☠️ BattlefieldLootScene initialized:', {
      battleId: this.battleId,
      battlefieldInstanceId: this.battlefieldInstanceId,
      battleIdFromData: !!data?.battleId,
      battleIdFromBattleStateManager: !!(!data?.battleId && BattleStateManager.getBattleId()),
      hasDummyData: !!this.dummyData
    });
  }

  async create() {
    const { width, height } = this.cameras.main;
    
    // Dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    
    // Use dummy data if provided, otherwise fetch from API
    if (this.dummyData) {
      console.log('🏴‍☠️ Using dummy data for BattlefieldLootScene');
      this.battlefieldLootData = this.dummyData;
    } else {
      await this.fetchBattlefieldLoot();
    }
    
    // Create UI
    this.createLootDisplay(width, height);
    
    // Start timer (delayed by 1 second for fade-in)
    this.startTime = this.time.now + 1000; // Add 1 second delay for fade-in
    
    // Auto-transition after duration (including fade-in delay)
    this.time.delayedCall(this.displayDuration + 1000, () => {
      // Update state to 'join' before returning to JoinScene
      sessionStateManager.updateState('join');
      
      BattleStateManager.reset();
      this.scene.start('JoinScene');
    });
  }

  async fetchBattlefieldLoot() {
    try {
      if (!this.battleId) {
        console.warn('⚠️ No battle ID available for fetching battlefield loot');
        return;
      }

      // Get JWT token
      const token = window.__SW_JWT_TOKEN__ || null;
      
      const response = await fetch(`/api/battles/${this.battleId}/battlefield-loot`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      if (!response.ok) {
        console.warn('⚠️ Failed to fetch battlefield loot');
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        this.battlefieldLootData = data.data;
        console.log('✅ Battlefield loot data fetched:', this.battlefieldLootData);
      }
    } catch (error) {
      console.error('❌ Error fetching battlefield loot:', error);
    }
  }

  createLootDisplay(width, height) {
    const container = this.add.container(width / 2, height / 2);
    
    // Title - much larger and centered (moved up)
    const title = this.add.text(0, -height / 2 + 60, 'Battlefield Loot Collected', {
      fontSize: '72px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6
    }).setOrigin(0.5);
    container.add(title);

    if (!this.battlefieldLootData || !this.battlefieldLootData.lootEntries || this.battlefieldLootData.lootEntries.length === 0) {
      // No loot message - larger and centered
      const noLootText = this.add.text(0, 0, 'No loot remained on the battlefield', {
        fontSize: '56px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);
      container.add(noLootText);
      
      // Create countdown progress bar at the bottom
      this.createProgressBar(width, height, container);
      return;
    }

    // Battlefield info (or unowned message) - larger
    if (this.battlefieldLootData.battlefield) {
      // Use custom name if available, otherwise template name
      const battlefieldName = this.battlefieldLootData.battlefield.customName || 
                             this.battlefieldLootData.battlefield.themeBattlefield?.name || 
                             'Unknown Battlefield';
      const ownerText = this.battlefieldLootData.battlefield.ownerUserId 
        ? `Owner: ${this.battlefieldLootData.battlefield.ownerUsername || 'Anonymous'}`
        : 'Owner: DaveY';
      
      const infoText = this.add.text(0, -height / 2 + 140, `${battlefieldName} - ${ownerText}`, {
        fontSize: '40px',
        color: '#8B5CF6',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);
      container.add(infoText);
    } else {
      // No battlefield instance - show unowned message - larger
      const unownedText = this.add.text(0, -height / 2 + 140, 'Unowned Battlefield - No loot team to collect', {
        fontSize: '40px',
        color: '#9CA3AF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0.5);
      container.add(unownedText);
    }

    // Display loot by type - properly aligned in columns
    const lootTypes = ['tokens', 'items', 'potions', 'gems'];
    const icons = ['💰', '📦', '🧪', '💎'];
    const colors = ['#F3F300', '#10B981', '#8B5CF6', '#3B82F6'];
    
    // Collect loot items that have data
    const lootItems = [];
    lootTypes.forEach((type, index) => {
      const total = this.battlefieldLootData.totals?.[type] || 0;
      const ownerKept = this.battlefieldLootData.ownerKept?.[type] || 0;
      
      if (total > 0) {
        lootItems.push({ type, index, total, ownerKept });
      }
    });
    
    if (lootItems.length === 0) {
      // No loot items to display
      return;
    }
    
    // Define column positions for consistent alignment
    const iconX = -350;      // Icons column (left)
    const labelX = -250;     // Labels column (center-left)
    const valuesX = 200;     // Values column (right, moved 200px to the right)
    
    // Calculate vertical spacing and centering
    const lootSpacing = 160;
    const totalLootHeight = lootItems.length * lootSpacing;
    const lootStartY = -totalLootHeight / 2 + lootSpacing / 2 + 50;
    
    // Display each loot item in aligned columns
    lootItems.forEach((lootItem, itemIndex) => {
      const { type, index, total, ownerKept } = lootItem;
      const y = lootStartY + itemIndex * lootSpacing;
      
      // Icon - aligned in left column (20% smaller: 120px * 0.8 = 96px)
      const iconText = this.add.text(iconX, y, icons[index], {
        fontSize: '96px',
        fontFamily: 'Arial, sans-serif'
      }).setOrigin(0.5);
      container.add(iconText);
      
      // Label - aligned in center-left column
      const labelText = this.add.text(labelX, y, type.charAt(0).toUpperCase() + type.slice(1), {
        fontSize: '48px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4
      }).setOrigin(0, 0.5); // Left-aligned within its column
      container.add(labelText);
      
      // Total dropped - aligned in right column (moved right 200px and down 35px)
      const totalText = this.add.text(valuesX, y, `Total Dropped: ${total.toLocaleString()}`, {
        fontSize: '40px',
        color: colors[0],
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
        align: 'left'
      }).setOrigin(0.5);
      container.add(totalText);
      
      // Owner kept - aligned in right column, below total (moved right 200px and down 35px)
      if (ownerKept > 0) {
        const keptText = this.add.text(valuesX, y + 50, `Owner Kept: ${ownerKept.toLocaleString()}`, {
          fontSize: '36px',
          color: '#10B981',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 4,
          align: 'left'
        }).setOrigin(0.5);
        container.add(keptText);
      }
    });

    // Create countdown progress bar at the bottom
    this.createProgressBar(width, height, container);
  }
  
  createProgressBar(width, height, container) {
    const barWidth = width * 0.8;
    const barHeight = 20;
    const barY = height - 60;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(width / 2, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.progressBarBg.setDepth(20);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    this.progressBarFill = this.add.rectangle(width / 2 - barWidth / 2, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.progressBarFill.setDepth(21);
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(width / 2, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    progressGlow.setDepth(19);
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
  }

  update(time, delta) {
    if (!this.startTime) return;
    
    const elapsed = time - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const progress = Math.max(0, Math.min(1, remaining / this.displayDuration));
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    // Update progress bar
    if (this.progressBarFill && this.progressBarBg) {
      const barWidth = this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      this.progressBarFill.x = this.progressBarBg.x - barWidth / 2;
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
    }
  }
}

